// src/components/RouteMap.jsx
// MapLibre GL JS (مجاني 100%) + OSRM للمسارات
import React, { useEffect, useRef, useCallback, useState } from "react";

// ── Lazy loader لـ MapLibre ──────────────────────────────────────────────────
let mlLoaded = false;
let mlLoading = false;
let mlCallbacks = [];
let mlError = null;

const loadMapLibre = (onSuccess, onError) => {
  if (mlLoaded)  { onSuccess(); return; }
  if (mlError)   { onError(mlError); return; }
  mlCallbacks.push({ onSuccess, onError });
  if (mlLoading) return;
  mlLoading = true;

  // CSS
  const link = document.createElement("link");
  link.rel   = "stylesheet";
  link.href  = "https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.css";
  document.head.appendChild(link);

  // JS
  const script  = document.createElement("script");
  script.src    = "https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.js";
  script.async  = true;
  script.onload = () => {
    mlLoaded  = true;
    mlLoading = false;
    mlCallbacks.forEach(({ onSuccess: cb }) => cb());
    mlCallbacks = [];
  };
  script.onerror = () => {
    mlError = "Failed to load MapLibre GL";
    mlLoading = false;
    mlCallbacks.forEach(({ onError: cb }) => cb(mlError));
    mlCallbacks = [];
  };
  document.head.appendChild(script);
};

const useMapLibre = () => {
  const [loaded, setLoaded] = useState(mlLoaded);
  const [error,  setError]  = useState(mlError);
  useEffect(() => {
    if (mlLoaded) { setLoaded(true); return; }
    const t = setTimeout(() => { if (!mlLoaded) setError("Map load timeout"); }, 10000);
    loadMapLibre(
      () => { clearTimeout(t); setLoaded(true); },
      (e) => { clearTimeout(t); setError(e); }
    );
    return () => clearTimeout(t);
  }, []);
  return { loaded, error };
};

const createLocationsControl = (onClick) => ({
  onAdd(map) {
    this._map = map;
    this._container = document.createElement("div");
    this._container.className = "maplibregl-ctrl maplibregl-ctrl-group";
    this._button = document.createElement("button");
    this._button.type = "button";
    this._button.title = "Show all locations";
    this._button.setAttribute("aria-label", "Show all locations");
    this._button.textContent = "↺";
    this._button.addEventListener("click", onClick);
    this._container.appendChild(this._button);
    return this._container;
  },
  onRemove() {
    this._button?.removeEventListener("click", onClick);
    this._container?.remove();
    this._map = undefined;
  },
});

// ── OSRM: حساب المسار مجاناً ────────────────────────────────────────────────
const fetchRoute = async (from, to) => {
  const url = `https://router.project-osrm.org/route/v1/driving/${from[1]},${from[0]};${to[1]},${to[0]}?overview=full&geometries=geojson`;
  const res  = await fetch(url);
  const data = await res.json();
  if (data.code !== "Ok" || !data.routes?.length) throw new Error("No route");
  return data.routes[0].geometry; // GeoJSON LineString
};

// ── Component ────────────────────────────────────────────────────────────────
const RouteMap = ({ fromCoords, toCoords, height = "300px" }) => {
  const containerRef = useRef(null);
  const mapRef       = useRef(null);   // maplibregl.Map instance
  const isMounted    = useRef(true);
  const initialized  = useRef(false);
  const { loaded, error } = useMapLibre();
  const [mapError, setMapError] = useState(null);

  // ── رسم المسار بعد init أو عند التحديث ────────────────────────────────────
  const drawRoute = useCallback(async (map, from, to) => {
    const ml = window.maplibregl;
    if (!map || !ml) return;

    // ✅ دالة مساعدة
    const safeFitBounds = (b, options = {}) => {
      if (!b || !isMounted.current) return;
      try {
        map.fitBounds(b, {
          padding: options.padding ?? 60,
          maxZoom: options.maxZoom ?? 14,
          duration: options.duration ?? 800,
        });
      } catch (e) {
        console.warn("fitBounds error:", e);
      }
    };

    try {
      const geometry = await fetchRoute(from, to);
      if (!isMounted.current) return;

      if (map.getSource("route")) {
        map.getSource("route").setData({ type: "Feature", geometry });
      } else {
        map.addSource("route", {
          type: "geojson",
          data: { type: "Feature", geometry },
        });
        map.addLayer({
          id: "route-outline",
          type: "line",
          source: "route",
          layout: { "line-join": "round", "line-cap": "round" },
          paint: { "line-color": "#fff", "line-width": 8, "line-opacity": 0.35 },
        });
        map.addLayer({
          id: "route",
          type: "line",
          source: "route",
          layout: { "line-join": "round", "line-cap": "round" },
          paint: { "line-color": "#ff6b00", "line-width": 5, "line-opacity": 0.9 },
        });
      }

      const coords = geometry.coordinates;
      const bounds = coords.reduce(
        (b, c) => b.extend(c),
        new ml.LngLatBounds(coords[0], coords[0])
      );

      // ✅ fitBounds أولي
      safeFitBounds(bounds);

      // ✅ إعادة fitBounds بعد tick
      setTimeout(() => {
        if (!isMounted.current) return;
        map.resize();
        safeFitBounds(bounds);
      }, 200);

    } catch (err) {
      if (!isMounted.current) return;
      console.warn("OSRM failed, drawing straight line:", err.message);

      const geojson = {
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: [
            [from[1], from[0]],
            [to[1], to[0]],
          ],
        },
      };

      if (map.getSource("route")) {
        map.getSource("route").setData(geojson);
      } else {
        map.addSource("route", { type: "geojson", data: geojson });
        map.addLayer({
          id: "route",
          type: "line",
          source: "route",
          layout: { "line-join": "round", "line-cap": "round" },
          paint: {
            "line-color": "#ff6b00",
            "line-width": 4,
            "line-opacity": 0.7,
            "line-dasharray": [2, 2],
          },
        });
      }

      // ✅ fitBounds في حالة الفشل كمان
      const fallbackBounds = new ml.LngLatBounds(
        [from[1], from[0]],
        [to[1], to[0]]
      );
      safeFitBounds(fallbackBounds);
    }

    // تحديث markers
    const markers = mapRef._markers;
    if (markers) {
      markers[0]?.setLngLat([from[1], from[0]]);
      markers[1]?.setLngLat([to[1], to[0]]);
    }
  }, []);

  // ── تهيئة الخريطة ──────────────────────────────────────────────────────────
  const initMap = useCallback(() => {
    if (!loaded || !containerRef.current || !fromCoords || !toCoords) return;
    if (initialized.current) return;

    const ml   = window.maplibregl;
    const from = [fromCoords[0], fromCoords[1]];
    const to   = [toCoords[0],   toCoords[1]];

    if (typeof ml?.supported === "function" && !ml.supported()) {
      setMapError("WebGL is unavailable in this browser");
      return;
    }

    initialized.current = true;

    let map;
    try {
      map = new ml.Map({
        container: containerRef.current,
        style: "https://tiles.openfreemap.org/styles/liberty",  // مجاني 100%
        center: [from[1], from[0]],
        zoom: 6,
        attributionControl: false,
      });
    } catch (mapInitError) {
      initialized.current = false;
      setMapError("WebGL is unavailable in this browser");
      console.error("RouteMap initialization failed:", mapInitError);
      return;
    }

    map.addControl(new ml.NavigationControl(), "top-right");
    map.addControl(new ml.FullscreenControl(), "top-right");
    map.addControl(
      createLocationsControl(() => {
        if (mapRef._locationsBounds) {
          map.fitBounds(mapRef._locationsBounds, { padding: 50, maxZoom: 14 });
        }
      }),
      "top-right"
    );
    map.addControl(new ml.ScaleControl({ unit: "metric" }), "bottom-left");
    map.addControl(
      new ml.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: false,
      }),
      "top-right"
    );
    // Attribution بسيطة
    map.addControl(new ml.AttributionControl({ compact: true }), "bottom-right");

    mapRef.current = map;
    mapRef._locationsBounds = new ml.LngLatBounds(
      [from[1], from[0]],
      [to[1], to[0]]
    );

    // Markers
    const fromEl = document.createElement("div");
    fromEl.innerHTML = `<div style="width:32px;height:32px;border-radius:50% 50% 50% 0;background:#22c55e;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.4);transform:rotate(-45deg);display:flex;align-items:center;justify-content:center"><span style="transform:rotate(45deg);color:#fff;font-weight:700;font-size:12px">1</span></div>`;

    const toEl = document.createElement("div");
    toEl.innerHTML = `<div style="width:32px;height:32px;border-radius:50% 50% 50% 0;background:#ef4444;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.4);transform:rotate(-45deg);display:flex;align-items:center;justify-content:center"><span style="transform:rotate(45deg);color:#fff;font-weight:700;font-size:12px">2</span></div>`;

    const fromMarker = new ml.Marker({ element: fromEl }).setLngLat([from[1], from[0]]).addTo(map);
    const toMarker   = new ml.Marker({ element: toEl   }).setLngLat([to[1],   to[0]  ]).addTo(map);

    // نحفظ الـ markers على mapRef علشان نقدر نحدثهم
    mapRef._markers = [fromMarker, toMarker];

    // ✅ رسم المسار بعد ما الخريطة تخلص تحمّل الـ style
    map.on("load", () => {
      if (!isMounted.current) return;
      drawRoute(map, from, to);
    });

    // ✅ الحل الرئيسي لمشكلة الـ mobile:
    // لما الـ container يظهر (offcanvas/accordion) نعمل resize
    const resizeObserver = new ResizeObserver(() => {
      if (
        containerRef.current &&
        containerRef.current.offsetWidth > 0 &&
        isMounted.current
      ) {
        map.resize();
        // ✅ إعادة fitBounds بعد resize
        if (mapRef._locationsBounds) {
          try {
            map.fitBounds(mapRef._locationsBounds, {
              padding: 60,
              maxZoom: 14,
              duration: 400,
            });
          } catch (_) {
            return;
          }
        }
      }
    });
    resizeObserver.observe(containerRef.current);
    mapRef._resizeObserver = resizeObserver;

  }, [loaded, drawRoute]); // fromCoords/toCoords مش هنا — init مرة واحدة

  // ── Update عند تغيير الإحداثيات ────────────────────────────────────────────
  const updateRoute = useCallback(() => {
    if (!initialized.current || !mapRef.current) return;
    if (!fromCoords || !toCoords) return;
    const from = [fromCoords[0], fromCoords[1]];
    const to   = [toCoords[0],   toCoords[1]];
    // لو الـ style لسه بيتحمّل، استنى الـ load event
    if (!mapRef.current.isStyleLoaded()) {
      mapRef.current.once("load", () => drawRoute(mapRef.current, from, to));
    } else {
      drawRoute(mapRef.current, from, to);
    }
  }, [fromCoords, toCoords, drawRoute]);

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (loaded && !initialized.current) initMap();
  }, [loaded, initMap]);

  useEffect(() => {
    if (initialized.current) updateRoute();
  }, [fromCoords, toCoords]); // eslint-disable-line

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      mapRef._resizeObserver?.disconnect();
      mapRef._markers?.forEach((m) => m.remove());
      mapRef.current?.remove();
      mapRef.current   = null;
      mapRef._locationsBounds = null;
      initialized.current = false;
    };
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────
  if (error || mapError) {
    return (
      <div className="map-error text-center p-4">
        <p className="text-danger">⚠️ {error || mapError}</p>
        <p className="text-muted small">
          Please enable hardware acceleration or use a browser with WebGL support.
        </p>
      </div>
    );
  }

  if (!loaded) {
    return (
      <div className="map-loading text-center p-4">
        <div className="spinner-border text-warning" role="status">
          <span className="visually-hidden">Loading map...</span>
        </div>
        <p className="mt-2 text-muted">Loading map...</p>
      </div>
    );
  }

  return (
    <div className="route-map-wrapper" style={{ position: "relative" }}>
      <div
        ref={containerRef}
        style={{
          height,
          width: "100%",
          borderRadius: "12px",
          overflow: "hidden",  // ✅ مهم لـ MapLibre + border-radius
          minHeight: "200px",
        }}
        aria-label="Map showing pickup and delivery locations"
      />
      {/* {fromCoords && toCoords && (
        <div className="map-attribution small text-muted text-center mt-1">
          <span>© <a href="https://openfreemap.org" target="_blank" rel="noreferrer">OpenFreeMap</a> © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a></span>
        </div>
      )} */}
    </div>
  );
};

// ── React.memo ─────────────────────────────────────────────────────────────
const MemoizedRouteMap = React.memo(RouteMap, (prev, next) => {
  const fromSame = prev.fromCoords?.[0] === next.fromCoords?.[0] && prev.fromCoords?.[1] === next.fromCoords?.[1];
  const toSame   = prev.toCoords?.[0]   === next.toCoords?.[0]   && prev.toCoords?.[1]   === next.toCoords?.[1];
  return fromSame && toSame && prev.height === next.height;
});

export { RouteMap, MemoizedRouteMap };
export default MemoizedRouteMap;